import React, { useEffect, useState, useCallback } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { api } from "@/lib/api";
import { useTableSort } from "@/lib/tableSort";
import { useAuth } from "@/context/AuthContext";
import { COLORS } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";

function TaskReport() {
  const [rep, setRep] = useState(null);
  const t = useTableSort(rep?.rows || []);
  useEffect(() => {
    api.get("/tasks/report").then(({ data }) => setRep(data)).catch(() => setRep(null));
  }, []);
  if (!rep || rep.rows.length === 0) return null;
  return (
    <Card className="p-5 border border-border" data-testid="task-report">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h3 className="font-display font-semibold text-sm">Arama Görevleri Raporu</h3>
        <div className="flex gap-2 text-xs">
          <span className="px-2 py-1 rounded bg-muted">Atanan: <b>{rep.totals.assigned}</b></span>
          <span className="px-2 py-1 rounded bg-emerald-500/10 text-emerald-600">Tamamlanan: <b>{rep.totals.done}</b></span>
          <span className="px-2 py-1 rounded bg-amber-500/10 text-amber-600">Açık: <b>{rep.totals.open}</b></span>
          <span className="px-2 py-1 rounded bg-red-500/10 text-red-600" data-testid="task-report-overdue">Süresi geçen: <b>{rep.totals.overdue}</b></span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-muted-foreground text-xs border-b border-border">
              <t.Th sortKey="t-name" get={(r) => r.name} text className="text-left py-2">Sorumlu</t.Th>
              <t.Th sortKey="t-assigned" get={(r) => r.assigned} className="text-center py-2">Atanan</t.Th>
              <t.Th sortKey="t-done" get={(r) => r.done} className="text-center py-2">Tamamlanan</t.Th>
              <t.Th sortKey="t-open" get={(r) => r.open} lowBest className="text-center py-2">Açık</t.Th>
              <t.Th sortKey="t-overdue" get={(r) => r.overdue} lowBest className="text-center py-2">Süresi Geçen</t.Th>
              <t.Th sortKey="t-pct" get={(r) => r.done_pct} className="text-left py-2 w-40">Tamamlama</t.Th>
            </tr>
          </thead>
          <tbody>
            {t.sorted.map((r) => (
              <tr key={r.id} className="border-b border-border/40" data-testid={`task-report-row-${r.id}`}>
                <td className="py-2">{r.name}</td>
                <td className="text-center py-2 tabular-nums">{r.assigned}</td>
                <td className="text-center py-2 tabular-nums text-emerald-600 font-semibold">{r.done}</td>
                <td className="text-center py-2 tabular-nums text-amber-600">{r.open}</td>
                <td className={`text-center py-2 tabular-nums font-semibold ${r.overdue > 0 ? "text-red-600" : "text-muted-foreground"}`}>{r.overdue}</td>
                <td className="py-2">
                  <div className="flex items-center gap-2">
                    <Progress value={r.done_pct} className="h-2 flex-1" />
                    <span className="text-xs tabular-nums w-10 text-right">%{r.done_pct}</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export default function TeamPerformance() {
  const { committee, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (user.role === "super_admin" && committee !== "all") params.committee_no = committee;
    try {
      const { data } = await api.get("/dashboard/stats", { params });
      setStats(data);
    } finally {
      setLoading(false);
    }
  }, [committee, user.role]);

  useEffect(() => { if (committee !== null) load(); }, [load, committee]);

  if (loading || !stats) return <div className="grid gap-4">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)}</div>;

  const chartData = stats.team.map((t) => ({ name: t.name, Arandı: t.contacted, Bekliyor: Math.max(t.assigned - t.contacted, 0), Kesin: t.kesin }));

  return (
    <div className="space-y-6" data-testid="team-page">
      <div>
        <h1 className="font-display text-2xl font-bold text-foreground">Takım Performansı</h1>
        <p className="text-sm text-muted-foreground">Sorumlu bazında arama ve dönüşüm takibi</p>
      </div>

      <Card className="p-5 border border-border">
        <h3 className="font-display font-semibold text-sm mb-4">Sorumlu Bazında Arama Durumu</h3>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="Arandı" stackId="a" fill={COLORS.cyan} radius={[0, 0, 0, 0]} />
            <Bar dataKey="Bekliyor" stackId="a" fill={COLORS.GELMEYECEK} radius={[4, 4, 0, 0]} />
            <Bar dataKey="Kesin" fill={COLORS.KESIN} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <TaskReport />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {stats.team.map((t) => {
          const pct = t.assigned ? Math.round(t.contacted / t.assigned * 100) : 0;
          const kesinPct = t.assigned ? Math.round(t.kesin / t.assigned * 100) : 0;
          return (
            <Card key={t.name} className="p-5 border border-border" data-testid={`team-card-${t.name}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-[#0F2C59] text-white grid place-items-center font-semibold">{t.name.slice(0, 2).toUpperCase()}</div>
                  <div>
                    <p className="font-semibold text-foreground">{t.name}</p>
                    <p className="text-xs text-muted-foreground">{t.assigned} firma sorumluluğu</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-display text-2xl font-bold" style={{ color: COLORS.cyan }}>%{pct}</p>
                  <p className="text-[11px] text-muted-foreground">arandı</p>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-xs mb-1"><span className="text-muted-foreground">Arama tamamlama</span><span>{t.contacted}/{t.assigned}</span></div>
                  <Progress value={pct} className="h-2" />
                </div>
                <div>
                  <div className="flex justify-between text-xs mb-1"><span className="text-muted-foreground">Kesin oy oranı</span><span>{t.kesin} kesin (%{kesinPct})</span></div>
                  <Progress value={kesinPct} className="h-2 [&>div]:bg-emerald-500" />
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
